/**
 * Core log entry type
 */
export interface LogEntry {
  /** Unique identifier (UUID) */
  id: string;
  /** Timestamp in milliseconds */
  timestamp: number;
  /** Labels as key-value pairs */
  labels: [string, string][];
  /** Raw log text */
  log: string;
}

/**
 * Display settings for the logs viewer
 */
export interface LogsSettings {
  /** Show timestamp column */
  showTimestamp: boolean;
  /** Show labels column */
  showLabels: boolean;
  /** Wrap long log lines */
  wrapLines: boolean;
  /** Prettify JSON content */
  prettifyJSON: boolean;
  /** Sort order */
  orderBy: "asc" | "desc";
  /** Saved query for persistence */
  savedQuery?: string;
  /** Saved from time for persistence */
  savedFrom?: string;
  /** Saved to time for persistence */
  savedTo?: string;
}

/**
 * Default settings values
 */
export const DEFAULT_LOGS_SETTINGS: LogsSettings = {
  showTimestamp: false,
  showLabels: false,
  wrapLines: false,
  prettifyJSON: false,
  orderBy: "desc",
};

/**
 * WebSocket connection status
 */
export type WebSocketStatus = "disconnected" | "connecting" | "connected" | "error";

/**
 * API response types
 */
export interface LogsStream {
  stream: Record<string, string>;
  values: [string, string][]; // [timestamp_ns, log_line]
}

export interface LogsQueryResponse {
  status: "success";
  data: {
    resultType: "streams";
    result: LogsStream[];
  };
}

export interface LogsLiveResponse {
  streams: LogsStream[];
}

/**
 * Label value types for LogQL
 */
export type LabelValue = string | { regex: string } | { notEqual: string } | { notRegex: string };

/**
 * Parsed LogQL query structure
 */
export interface ParsedQuery {
  labels: Record<string, LabelValue>;
  pipeline: string;
}

/**
 * Filter option for dropdowns
 */
export interface FilterOption {
  value: string;
  label: string;
  disabled?: boolean;
  group?: string;
}

/**
 * Hook options
 */
export interface UseLogsOptions {
  /** Organization name */
  org: string;
  /** Default LogQL query */
  defaultQuery: string;
  /** Storage key for localStorage persistence */
  storageKey: string;
  /** Optional prefix for download filenames */
  fileNamePrefix?: string;
  /** Logs service endpoint */
  logsEndpoint: string;
  /** Grafana service endpoint */
  grafanaEndpoint: string;
  /** Optional check before query execution */
  beforeQuery?: () => boolean;
  /** Timezone for resolving time expressions (e.g., "UTC" or "America/New_York") */
  timezone: string;
}

export interface UseLogsSettingsOptions {
  /** Storage key for localStorage */
  storageKey: string;
}

export interface UseLogsQueryOptions {
  /** Organization name */
  org: string;
  /** Logs service endpoint */
  logsEndpoint: string;
  /** Timezone for resolving time expressions (e.g., "UTC" or "America/New_York") */
  timezone: string;
}

export interface UseLogsWebSocketOptions {
  /** Organization name */
  org: string;
  /** Logs service endpoint */
  logsEndpoint: string;
  /** Callback when new entries arrive */
  onMessage: (entries: LogEntry[]) => void;
  /** Callback on error */
  onError?: (error: Error) => void;
  /** Whether the hook is enabled */
  enabled?: boolean;
}

/**
 * Hook return types
 */
export interface UseLogsSettingsReturn {
  settings: LogsSettings;
  updateSettings: (updates: Partial<LogsSettings>) => void;
}

export interface UseLogsQueryReturn {
  execute: (query: string, from: string, to: string) => Promise<LogEntry[] | null>;
  cancel: () => void;
  isLoading: boolean;
  error: Error | null;
}

export interface UseLogsWebSocketReturn {
  connect: (query: string, start?: string) => void;
  disconnect: () => void;
  status: WebSocketStatus;
}

export interface UseLogsReturn {
  // Data
  logs: LogEntry[];
  commonLabels: string[];
  hasUniqueLabels: boolean;

  // Query state
  query: string;
  setQuery: (q: string) => void;
  from: string | null;
  to: string | null;
  setFrom: (from: string | null) => void;
  setTo: (to: string | null) => void;

  // Loading states
  isLoading: boolean;
  isLive: boolean;
  isLiveDirty: boolean;
  liveStatus: WebSocketStatus;
  hasExecuted: boolean;

  // Actions
  executeQuery: () => Promise<void>;
  cancelQuery: () => void;
  startLive: () => void;
  stopLive: () => void;
  restartLive: () => void;
  downloadLogs: (format: "json" | "txt") => void;
  copyLogs: () => Promise<void>;
  resetQuery: () => void;

  // Settings
  settings: LogsSettings;
  updateSettings: (s: Partial<LogsSettings>) => void;

  // Query helpers
  setQueryKeyValue: (key: string, value: string, remove?: boolean) => void;
  getQueryKeyValue: (key: string) => string | null;

  // Grafana
  openInGrafana: () => void;
  grafanaRedirectUrl: string;

  // Error
  error: string | null;

  // Live pause (scroll-based buffering)
  bufferedCount: number;
  setLivePaused: (paused: boolean) => void;
}

/**
 * Component props
 */
export interface LogsViewerProps {
  logs: LogEntry[];
  isLoading?: boolean;
  isLive?: boolean;
  liveStatus?: WebSocketStatus;
  settings: LogsSettings;
  commonLabels?: string[];
  width: number;
  error?: string | null;
  hasExecuted?: boolean;
  hasUniqueLabels?: boolean;
  /** Number of buffered log entries waiting while live is paused */
  bufferedCount?: number;
  /** Called when scroll position changes relative to the "new logs" edge */
  onPauseChange?: (paused: boolean) => void;
  /** Called when user clicks the "new logs" banner to resume */
  onResume?: () => void;
  /** Called when a label is clicked to add it to the filter */
  onLabelClick?: (key: string, value: string) => void;
}

export interface LogRowProps {
  entry: LogEntry;
  index: number;
  showTimestamp: boolean;
  showLabels: boolean;
  wrapLines: boolean;
  prettifyJSON: boolean;
  labelsWidth: number;
  commonLabels: string[];
  onLabelClick?: (key: string, value: string) => void;
}

export interface LogLabelsProps {
  labels: [string, string][];
  width: number;
  onLabelClick?: (key: string, value: string) => void;
}

export interface LogContentProps {
  text: string;
  wrap: boolean;
  prettifyJSON: boolean;
}

export interface LogTimestampProps {
  timestamp: number;
}

export interface LogsToolbarProps {
  query: string;
  onQueryChange: (query: string) => void;
  from: string | null;
  to: string | null;
  onFromChange: (from: string | null) => void;
  onToChange: (to: string | null) => void;
  onExecute: () => void;
  onCancel: () => void;
  onLive: () => void;
  onStopLive: () => void;
  onRestartLive: () => void;
  isLoading: boolean;
  isLive: boolean;
  isLiveDirty: boolean;
  liveStatus: WebSocketStatus;
  onDownload: (format: "json" | "txt") => void;
  onOpenGrafana: () => void;
  onReset: () => void;
  disabled?: boolean;
  filterSlot?: React.ReactNode;
  onQueryFocus?: () => void;
  onQueryBlur?: () => void;
}

export interface LogsSettingsProps {
  settings: LogsSettings;
  onChange: (settings: Partial<LogsSettings>) => void;
  hasUniqueLabels: boolean;
}

export interface LogsFilterProps {
  label: string;
  value: string;
  options: FilterOption[];
  onChange: (value: string) => void;
  placeholder?: string;
}

export interface LogsPageProps {
  org: string;
  defaultQuery: string;
  storageKey: string;
  fileNamePrefix?: string;
  logsEndpoint: string;
  grafanaEndpoint: string;
  beforeQuery?: () => boolean;
  filterSlot?: React.ReactNode;
  disabled?: boolean;
  disabledMessage?: string;
  /** Filter configurations for bidirectional sync with query */
  filters?: FilterSyncConfig[];
}

/**
 * Filter configuration for bidirectional sync with query
 */
export interface FilterSyncConfig {
  /** The label key in the LogQL query (e.g., "location", "container") */
  key: string;
  /** Current filter value from dropdown state */
  value: string;
  /** Callback to update dropdown state */
  onChange: (value: string) => void;
  /** Value that means "any" / no filter (will remove the key from query) */
  anyValue?: string;
  /** Value that means custom (won't update query automatically) */
  customValue?: string;
  /** Available options for validation */
  options?: Array<{ value: string }>;
}
